// src/components/admin/goal/steps/Step3.js

import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  TextField,
  MenuItem,
  Avatar,
  Snackbar,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import StepNavigation from './StepNavigation';

dayjs.locale("pt-br");

export default function Step3({ goal, absences, setAbsences, onBack, onNext, activeStep, sellers, setSellers }) {
  const [month] = useState(dayjs(`${goal.period}-01`));

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [absenceType, setAbsenceType] = useState("Folga");

  const daysInMonth = month.daysInMonth();
  const firstDay = month.startOf("month").day(); // 0-domingo
  const S3_URL = process.env.REACT_APP_S3_URL;

  const absenceColors = {
    day_off: "#607D8B",   // Folga
    leave: "#ff5a5a",     // Falta
    vacation: "#b2b2b2",  // Férias
    other: "#9c27b0",     // Outro
  };

  useEffect(() => {
    const fetchAbsences = async () => {
      setLoading(true);
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      try {
        // executa as requisições em paralelo
        const [absencesRes, usersRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}admin/absence/${goal.start_date}/${goal.end_date}`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}admin/user/`, { headers }),
        ]);
        setAbsences(absencesRes.data);
        setSellers(usersRes.data.filter(user => user.role === "seller"));
      } catch (err) {
        console.error("Erro na requisição:", err);
        setSnackbar({
          open: true,
          message: "Erro ao carregar informações",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchAbsences();
  // eslint-disable-next-line
  }, [setAbsences]);

  const handleOpen = (date) => {
    setSelectedDate(date);
    setOpen(true);
  };

  const handleSave = async () => {
    if (selectedDate && selectedUser) {
      setLoading(true);
      try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}admin/absence`, {
          date: selectedDate,
          user_id: selectedUser,
          type: absenceType,
        });
        setAbsences([...absences, data]);
        setSnackbar({ open: true, message: "Ausência adicionada com sucesso!", severity: "success" });
      } catch (err) {
        setSnackbar({ open: true, message: "Erro ao salvar ausência", severity: "error" });
      } finally {
        setLoading(false);
        setOpen(false);
        setSelectedUser(null);
        setAbsenceType('');
      }
    }
  };

  const handleRemove = async (id) => {
    setLoading(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}admin/absence/${id}`);
      setAbsences(absences.filter((a) => a.id !== id));
      setSnackbar({ open: true, message: "Ausência removida com sucesso!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Erro ao remover ausência", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const renderCells = () => {
    const cells = [];
    // espaços antes do primeiro dia
    for (let i = 0; i < firstDay; i++) {
      cells.push(<Box key={`empty-${i}`} sx={{ minHeight: 120, border: "1px solid #ddd", bgcolor:  "#f0f0f0", opacity: 0.5 }} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = month.date(day).format("YYYY-MM-DD");
      const dailyAbsences = absences.filter((a) => a.date === date);

      // verificar se está no range
      const inRange =
        dayjs(date).isSameOrAfter(goal.start_date) &&
        dayjs(date).isSameOrBefore(goal.end_date);

      cells.push(
        <Box
          key={date}
          sx={{
            border: "1px solid #ddd",
            minHeight: 120,
            p: 1,
            cursor: inRange ? "pointer" : "default",
            bgcolor: !inRange ? "#f0f0f0" : "",
            opacity: inRange ? 1 : 0.5,
            overflow: "hidden",        // evita estourar o box
            wordBreak: "break-word",   // permite quebra dentro das palavras longas
            display: "flex",
            flexDirection: "column",   // garante que empilhe verticalmente
          }}
          onClick={() => inRange && handleOpen(date)}
        >
          <Typography variant="subtitle2">{day}</Typography>
          {dailyAbsences.map((a, idx) => {
            const user = sellers.find((v) => v.id === a.user_id);
            return (
              <Box
                key={idx}
                sx={{
                  mt: 0.5,
                  bgcolor: absenceColors[a.type],
                  color: "white",
                  pl: 0.5,
                  pt: 0.5,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  display: "flex",
                  flexDirection: "column", // empilha header em cima e nome embaixo
                  // gap: 0.5,
                }}
              >
                {/* Header: avatar + X */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Avatar
                    src={`${S3_URL}/img/user/${user?.avatar_url}`}
                    alt={user?.name}
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: "50%", 
                      boxShadow: "0 0 3px rgba(0,0,0,0.5)",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(a.id);
                    }}
                    sx={{ color: "white", p: "2px" }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>

                {/* Nome do usuário embaixo */}
                <Typography
                  variant="caption"
                  sx={{
                    // textAlign: "center",  // centraliza se quiser
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    fontSize: "0.7rem",
                  }}
                >
                  {user?.name}
                </Typography>
              </Box>

            );
          })}
        </Box>
      );
    }

    return cells;
  };

  return (
    <>
      <Typography 
        variant="h6" 
        sx={{ mb: 2, textAlign: "center", mt: 4 }}
      >
        Ausências em {month.format("MMMM YYYY")}:
      </Typography>

      {/* grade fixa de 7 colunas */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
          }}
        >
          {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
            <Box
              key={d}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                p: 1,
                bgcolor: "#f5f5f5",
                border: "1px solid #ddd",
              }}
            >
              {d}
            </Box>
          ))}
          {renderCells()}
        </Box>
      </Paper>

      <Box sx={{ mt: 2, display: "flex", gap: 3, flexWrap: "wrap" }}>
        {[
          { type: "leave", label: "Falta" },
          { type: "day_off", label: "Folga" },
          { type: "vacation", label: "Férias" },
          { type: "other", label: "Outro" },
        ].map((item) => (
          <Box key={item.type} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: 0.5,
                bgcolor: absenceColors[item.type],
              }}
            />
            <Typography variant="body2">{item.label}</Typography>
          </Box>
        ))}
      </Box>


      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Adicionar Ausência</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Vendedor"
            value={selectedUser || ""}
            onChange={(e) => setSelectedUser(Number(e.target.value))}
            sx={{ mt: 2 }}
          >
            {sellers
              // ativos no dia selecionado
              .filter((v) => {
                const startOk = dayjs(v.start_date).isSameOrBefore(selectedDate);
                const endOk = !v.end_date || dayjs(v.end_date).isSameOrAfter(selectedDate);
                return startOk && endOk;
              })
              // remove quem já está ausente nesse dia
              .filter((v) => !absences.some((a) => a.date === selectedDate && a.user_id === v.id))
              // ordena pelo nome
              .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }))
              .map((v) => (
              <MenuItem key={v.id} value={v.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar src={`${S3_URL}/img/user/${v.avatar_url}`} alt={v.name} sx={{ width: 24, height: 24 }} />
                  {v.name}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Motivo"
            value={absenceType}
            onChange={(e) => setAbsenceType(e.target.value)}
            sx={{ mt: 2 }}
          >
            <MenuItem value=""></MenuItem>
            <MenuItem value="leave">Falta</MenuItem>
            <MenuItem value="day_off">Folga</MenuItem>
            <MenuItem value="vacation">Férias</MenuItem>
            <MenuItem value="other">Outro</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSelectedUser(null);
            setAbsenceType('');
            setOpen(false);
          }}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <StepNavigation 
        onBack={onBack}
        onNext={onNext}
        activeStep={activeStep}
      />
    </>
  );
}
